import type { PersonDto } from "@caloteiros/shared";
import { PIX_KEY_TYPES } from "@caloteiros/shared";
import type { PixKeyType } from "@caloteiros/shared";
import { useState } from "react";
import type { ReactElement } from "react";
import { PageHeader } from "../components/AppShell";
import { IconLink, IconPlus, IconTrash } from "../components/Icons";
import { Avatar, ConfirmDialog, EmptyState, Field, Sheet, Skeleton } from "../components/ui";
import { ApiError } from "../lib/api";
import { firstName, pixTypeLabel } from "../lib/format";
import {
  useArchivePerson,
  useCreatePerson,
  useLinkPerson,
  useMergePeople,
  usePeople,
  useUnlinkPerson,
  useUpdatePerson,
} from "../lib/queries";
import { useToast } from "../lib/toast";

export function PeoplePage(): ReactElement {
  const toast = useToast();
  const people = usePeople();
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const linkPerson = useLinkPerson();
  const unlinkPerson = useUnlinkPerson();
  const mergePeople = useMergePeople();
  const archivePerson = useArchivePerson();

  const [createOpen, setCreateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PersonDto | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PersonDto | null>(null);

  const [draftName, setDraftName] = useState("");
  const [draftPixType, setDraftPixType] = useState<PixKeyType | "">("");
  const [draftPixKey, setDraftPixKey] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [mergeSource, setMergeSource] = useState("");
  const [mergeTarget, setMergeTarget] = useState("");

  const list = people.data ?? [];
  const others = list.filter((person) => !person.isMe);

  const openEdit = (person: PersonDto): void => {
    setEditTarget(person);
    setDraftName(person.displayName);
    setDraftPixType(person.pixKeyType ?? "");
    setDraftPixKey(person.pixKey ?? "");
    setLinkEmail("");
  };

  const handleCreate = async (): Promise<void> => {
    if (!draftName.trim()) return;
    try {
      await createPerson.mutateAsync({
        displayName: draftName.trim(),
        pixKeyType: draftPixType === "" ? null : draftPixType,
        pixKey: draftPixKey.trim() === "" ? null : draftPixKey.trim(),
      });
      setCreateOpen(false);
      setDraftName("");
      setDraftPixType("");
      setDraftPixKey("");
      toast.success("Pessoa cadastrada.");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui cadastrar.");
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editTarget) return;
    try {
      await updatePerson.mutateAsync({
        personId: editTarget.id,
        input: {
          displayName: draftName.trim(),
          pixKeyType: draftPixType === "" ? null : draftPixType,
          pixKey: draftPixKey.trim() === "" ? null : draftPixKey.trim(),
        },
      });
      toast.success("Dados atualizados.");
      setEditTarget(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui salvar.");
    }
  };

  return (
    <>
      <PageHeader
        title="Pessoas"
        actions={
          <button
            type="button"
            className="btn-icon"
            aria-label="Cadastrar pessoa"
            onClick={() => {
              setDraftName("");
              setDraftPixType("");
              setDraftPixKey("");
              setCreateOpen(true);
            }}
          >
            <IconPlus />
          </button>
        }
      />

      <main className="app-content stack-lg">
        <p className="hint">
          Seu caderninho de contatos. Cadastre quem quiser só pelo nome e reaproveite em
          qualquer rolê — vincular a uma conta de verdade é opcional, e reversível.
        </p>

        {people.isLoading ? (
          <Skeleton height={64} count={4} />
        ) : list.length === 0 ? (
          <EmptyState
            emoji="👥"
            title="Nenhuma pessoa ainda"
            description="Cadastre a galera do rolê para lançar despesas mais rápido."
          />
        ) : (
          <div className="card">
            {list.map((person) => (
              <button
                key={person.id}
                type="button"
                className="list-item"
                onClick={() => openEdit(person)}
              >
                <Avatar name={person.displayName} color={person.avatarColor} />
                <span className="grow">
                  <span className="list-item-title">
                    {person.displayName}
                    {person.isMe ? " (você)" : ""}
                  </span>
                  <span className="faint" style={{ display: "block" }}>
                    {person.isPlaceholder ? (
                      <span className="badge">sem conta</span>
                    ) : (
                      <span className="badge badge-brand">{person.linkedUserEmail}</span>
                    )}
                    {person.pixKey ? ` · ${pixTypeLabel(person.pixKeyType)}` : ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {others.length > 1 ? (
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setMergeOpen(true)}>
            Juntar cadastros duplicados
          </button>
        ) : null}
      </main>

      {/* ---- cadastrar ---- */}
      <Sheet open={createOpen} onClose={() => setCreateOpen(false)} title="Nova pessoa">
        <div className="stack-lg">
          <Field label="Nome">
            <input
              className="input"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              maxLength={80}
              autoFocus
            />
          </Field>
          <PixFields
            type={draftPixType}
            keyValue={draftPixKey}
            onType={setDraftPixType}
            onKey={setDraftPixKey}
          />
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleCreate()}
            disabled={createPerson.isPending}
          >
            Cadastrar
          </button>
        </div>
      </Sheet>

      {/* ---- editar / vincular ---- */}
      <Sheet
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        title={editTarget ? firstName(editTarget.displayName) : ""}
      >
        <div className="stack-lg">
          <Field label="Nome">
            <input
              className="input"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              maxLength={80}
            />
          </Field>

          <PixFields
            type={draftPixType}
            keyValue={draftPixKey}
            onType={setDraftPixType}
            onKey={setDraftPixKey}
          />

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleSaveEdit()}
            disabled={updatePerson.isPending}
          >
            Salvar
          </button>

          {editTarget && !editTarget.isMe ? (
            <div className="stack">
              <span className="label">Conta vinculada</span>
              {editTarget.isPlaceholder ? (
                <>
                  <p className="hint">
                    Se essa pessoa criou uma conta, informe o e-mail dela. O histórico
                    inteiro continua onde está.
                  </p>
                  <div className="row">
                    <input
                      className="input grow"
                      type="email"
                      inputMode="email"
                      autoCapitalize="none"
                      placeholder="amigo@email.com"
                      value={linkEmail}
                      onChange={(event) => setLinkEmail(event.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-subtle"
                      onClick={() => {
                        linkPerson.mutate(
                          { personId: editTarget.id, email: linkEmail.trim() },
                          {
                            onSuccess: () => {
                              toast.success("Vinculado.");
                              setEditTarget(null);
                            },
                            onError: (error) =>
                              toast.error(
                                error instanceof ApiError ? error.message : "Não consegui vincular.",
                              ),
                          },
                        );
                      }}
                    >
                      <IconLink width={18} height={18} />
                      Vincular
                    </button>
                  </div>
                </>
              ) : (
                <div className="row-between">
                  <span className="badge badge-brand">{editTarget.linkedUserEmail}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() =>
                      unlinkPerson.mutate(editTarget.id, {
                        onSuccess: () => {
                          toast.success("Vínculo desfeito.");
                          setEditTarget(null);
                        },
                        onError: () => toast.error("Não consegui desvincular."),
                      })
                    }
                  >
                    Desvincular
                  </button>
                </div>
              )}

              <button
                type="button"
                className="btn btn-danger btn-block"
                onClick={() => {
                  const target = editTarget;
                  setEditTarget(null);
                  setRemoveTarget(target);
                }}
              >
                <IconTrash width={18} height={18} />
                Remover do caderninho
              </button>
            </div>
          ) : null}
        </div>
      </Sheet>

      {/* ---- juntar duplicados ---- */}
      <Sheet open={mergeOpen} onClose={() => setMergeOpen(false)} title="Juntar cadastros">
        <div className="stack-lg">
          <p className="muted">
            Cadastrou a mesma pessoa duas vezes? Escolha qual some e qual fica: despesas,
            partes e acertos passam todos para quem ficar.
          </p>

          <Field label="Esta some">
            <select
              className="select"
              value={mergeSource}
              onChange={(event) => setMergeSource(event.target.value)}
            >
              <option value="">Escolha...</option>
              {others.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                </option>
              ))}
            </select>
          </Field>

          <Field label="E vira esta">
            <select
              className="select"
              value={mergeTarget}
              onChange={(event) => setMergeTarget(event.target.value)}
            >
              <option value="">Escolha...</option>
              {list
                .filter((person) => person.id !== mergeSource)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName}
                    {person.isMe ? " (você)" : ""}
                  </option>
                ))}
            </select>
          </Field>

          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={!mergeSource || !mergeTarget || mergePeople.isPending}
            onClick={() =>
              mergePeople.mutate(
                { sourceId: mergeSource, targetId: mergeTarget },
                {
                  onSuccess: () => {
                    toast.success("Cadastros juntados.");
                    setMergeOpen(false);
                    setMergeSource("");
                    setMergeTarget("");
                  },
                  onError: (error) =>
                    toast.error(error instanceof ApiError ? error.message : "Não consegui juntar."),
                },
              )
            }
          >
            Juntar
          </button>
        </div>
      </Sheet>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remover do caderninho?"
        message="Se essa pessoa já aparece em alguma despesa, ela é apenas arquivada — o histórico dos rolês continua intacto."
        confirmLabel="Remover"
        destructive
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          const target = removeTarget;
          setRemoveTarget(null);
          if (!target) return;
          archivePerson.mutate(target.id, {
            onSuccess: () => toast.success("Removido."),
            onError: (error) =>
              toast.error(error instanceof ApiError ? error.message : "Não consegui remover."),
          });
        }}
      />
    </>
  );
}

/** Tipo + chave Pix, sempre juntos: chave sem tipo não ajuda ninguém a pagar. */
function PixFields({
  type,
  keyValue,
  onType,
  onKey,
}: {
  type: PixKeyType | "";
  keyValue: string;
  onType: (value: PixKeyType | "") => void;
  onKey: (value: string) => void;
}): ReactElement {
  return (
    <>
      <Field label="Tipo da chave Pix">
        <select
          className="select"
          value={type}
          onChange={(event) => onType(event.target.value as PixKeyType | "")}
        >
          <option value="">Sem chave Pix</option>
          {PIX_KEY_TYPES.map((option) => (
            <option key={option} value={option}>
              {pixTypeLabel(option)}
            </option>
          ))}
        </select>
      </Field>

      {type !== "" ? (
        <Field label="Chave Pix">
          <input
            className="input"
            value={keyValue}
            onChange={(event) => onKey(event.target.value)}
            autoCapitalize="none"
            maxLength={140}
          />
        </Field>
      ) : null}
    </>
  );
}
